// frontend/src/components/BacklogCalendarView.js
import React, { useState } from 'react';
import CalendarTaskItem from './CalendarTaskItem';

const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const BacklogCalendarView = ({ dates, tasks, objectives, projects, onDropOnDate }) => {
    const [draggedOverDate, setDraggedOverDate] = useState(null);

    const getTasksForDay = (day) => {
        const dayStr = day.toISOString().split('T')[0];
        const tasksForDay = tasks.filter(task => {
            const isDue = !task.is_completed && task.due_date === dayStr;
            const wasCompleted = task.is_completed && task.updated_at?.startsWith(dayStr);
            return isDue || wasCompleted;
        });
        return tasksForDay;
    };
    
    const todayStr = new Date().toISOString().split('T')[0];

    const handleDragOver = (e, day) => {
        e.preventDefault();
        setDraggedOverDate(day.toISOString());
    };

    const handleDragLeave = () => {
        setDraggedOverDate(null);
    };

    const handleDrop = (e, day) => {
        e.preventDefault();
        setDraggedOverDate(null);
        const task = JSON.parse(e.dataTransfer.getData('application/json'));
        if (task && onDropOnDate) {
            onDropOnDate(task, day);
        }
    };

    // Determine grid dimensions based on provided dates
    const columns = dates.length >= 7 ? 7 : dates.length || 1;
    const rows = dates.length > 7 ? Math.ceil(dates.length / 7) : 1;

    // Tailwind requires explicit class names; map supported values
    const colClass = columns === 7 ? 'grid-cols-7' : columns === 6 ? 'grid-cols-6' : columns === 5 ? 'grid-cols-5' : columns === 4 ? 'grid-cols-4' : columns === 3 ? 'grid-cols-3' : columns === 2 ? 'grid-cols-2' : 'grid-cols-1';
    const rowClass = rows === 2 ? 'grid-rows-2' : rows === 3 ? 'grid-rows-3' : 'grid-rows-1';

    // Header labels: full week for >= 7 days, otherwise labels for the given dates
    const headerLabels = dates.length >= 7
        ? weekDays
        : dates.map(d => d.toLocaleDateString('fr-FR', { weekday: 'long' }));

    return (
        <div className="h-full flex flex-col">
            <div className={`grid ${colClass} flex-shrink-0`}>
                {headerLabels.map((day, idx) => (
                    <div key={`${day}-${idx}`} className="text-center text-xs font-bold text-gray-500 py-1 border-b capitalize">
                        {day}
                    </div>
                ))}
            </div>
            <div className={`grid ${colClass} ${rowClass} flex-grow min-h-0`}>
                {dates.map(day => {
                    const tasksOfTheDay = getTasksForDay(day);
                    const isToday = day.toISOString().split('T')[0] === todayStr;
                    const isDraggedOver = draggedOverDate === day.toISOString();

                    return (
                        <div 
                            key={day.toString()} 
                            className={`border-r border-gray-200 p-1 flex flex-col transition-colors ${isToday ? 'bg-blue-50' : ''} ${isDraggedOver ? 'bg-blue-200 ring-2 ring-blue-400' : ''}`}
                            onDragOver={(e) => handleDragOver(e, day)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, day)}
                        >
                            <span className={`text-xs font-semibold flex-shrink-0 ${isToday ? 'text-blue-600' : 'text-gray-600'}`}>
                                {day.getDate()}
                            </span>
                            <div className="flex-grow overflow-y-auto overscroll-contain space-y-1 mt-1 min-h-0 pr-1" style={{ scrollbarGutter: 'stable' }}>
                                {tasksOfTheDay.map(task => {
                                    const objectiveForTask = objectives.find(o => String(o.id) === String(task.objective_id));
                                    const projectForObjective = objectiveForTask && projects ? projects.find(p => String(p.id) === String(objectiveForTask.project_id)) : null;
                                    const projectForTask = projectForObjective || (task.project_id && projects ? projects.find(p => String(p.id) === String(task.project_id)) : null);
                                    return <CalendarTaskItem key={task.id} task={task} objectiveForTask={objectiveForTask} projectForObjective={projectForTask} />;
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BacklogCalendarView;
